import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Hammer, Shield, Clock, Phone, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { ModernLogo } from "@/components/ModernLogo";
import carpenterImage from "@/assets/real-carpenter.jpg";

export const CarpentryPage = () => {
  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="Professional Carpentry Services - Skilled Carpenters | Sortable"
        description="Find skilled carpenters and joiners near you. Custom furniture, kitchen fitting, flooring, and structural carpentry work. Fully qualified and insured professionals."
        keywords="carpenter, carpentry services, joiner, custom furniture, kitchen fitting, flooring installation, door installation, window installation, bespoke carpentry, structural carpentry, cabinet making, timber framing, decking, stairs, shelving, wardrobe installation, fitted furniture, wood flooring, laminate flooring, skirting boards, architrave, local carpenter"
        canonical="https://sortable.uk/services/carpentry"
        ogImage="https://sortable.uk/real-carpenter.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Professional Carpentry Services",
          "description": "Custom carpentry and joinery work by skilled craftsmen",
          "provider": {
            "@type": "Organization",
            "name": "Sortable"
          },
          "serviceType": "Carpentry Services",
          "areaServed": {
            "@type": "Country",
            "name": "United Kingdom"
          }
        }}
      />

      {/* Header */}
      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ModernLogo showTagline={false} />
            </Link>
            <div className="flex items-center gap-1 md:gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <span className="hidden md:inline">Home</span>
                  <span className="md:hidden">Home</span>
                </Button>
              </Link>
              <Link to="/for-tradesmen">
                <Button variant="outline" size="sm">
                  <span className="hidden md:inline">For Tradesmen</span>
                  <span className="md:hidden">Tradesmen</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-600/10 border border-amber-600/20 text-amber-600 px-6 py-3 rounded-full text-lg font-semibold mb-8 shadow-elegant">
            <Hammer className="h-5 w-5" />
            Professional Carpentry Services
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-hero bg-clip-text text-transparent">
            Expert Carpenters & Joiners
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            From bespoke furniture to structural carpentry, find skilled carpenters and joiners for all your woodworking needs. Quality craftsmanship and attention to detail guaranteed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/create-job">
              <Button size="lg" className="bg-gradient-hero hover:shadow-glow transform hover:scale-105">
                <Users className="mr-2 h-5 w-5" />
                Find Carpenters Now
              </Button>
            </Link>
            <Link to="/for-tradesmen">
              <Button size="lg" variant="outline">
                <Hammer className="mr-2 h-5 w-5" />
                Join as a Carpenter
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Complete Carpentry Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our network of skilled carpenters provides comprehensive woodworking solutions for residential and commercial projects.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Custom Furniture",
                description: "Bespoke furniture design and creation tailored to your specific requirements",
                icon: <Hammer className="w-6 h-6" />
              },
              {
                title: "Kitchen Fitting",
                description: "Complete kitchen installation including cabinets, worktops, and accessories",
                icon: <CheckCircle className="w-6 h-6" />
              },
              {
                title: "Flooring Installation",
                description: "Hardwood, laminate, and engineered flooring installation services",
                icon: <Shield className="w-6 h-6" />
              },
              {
                title: "Door & Window Installation",
                description: "Professional installation of doors, windows, and frames",
                icon: <Hammer className="w-6 h-6" />
              },
              {
                title: "Built-in Storage",
                description: "Custom wardrobes, shelving, and storage solutions",
                icon: <CheckCircle className="w-6 h-6" />
              },
              {
                title: "Structural Carpentry",
                description: "Roof repairs, timber framing, and structural woodwork",
                icon: <Shield className="w-6 h-6" />
              }
            ].map((service, index) => (
              <Card key={index} className="border-border/50 hover:shadow-elegant transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-600/10 rounded-lg text-amber-600">
                      {service.icon}
                    </div>
                    <CardTitle className="text-lg">{service.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose Our Carpentry Network?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              All our carpenters are skilled craftsmen with years of experience in fine woodworking and construction.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Master Craftsmen</h4>
                    <p className="text-muted-foreground">
                      Time-served carpenters and joiners with extensive experience in fine woodworking.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Quality Materials</h4>
                    <p className="text-muted-foreground">
                      Only the finest timber and materials used for lasting quality and beauty.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Custom Solutions</h4>
                    <p className="text-muted-foreground">
                      Bespoke carpentry work designed to meet your exact specifications and style.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Fully Insured</h4>
                    <p className="text-muted-foreground">
                      Comprehensive insurance coverage and workmanship guarantees for peace of mind.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={carpenterImage} 
                alt="Professional carpenter working on custom furniture"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Need a Carpenter Today?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Post your carpentry project and get quotes from skilled, local carpenters. Quality craftsmanship and competitive pricing guaranteed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/create-job">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-white text-primary hover:bg-gray-100 font-semibold px-8 py-3 border-white"
                >
                  <Users className="mr-2 h-5 w-5" />
                  Get Quotes Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background border-t py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <ModernLogo showTagline={false} />
              <p className="text-muted-foreground mt-4 max-w-md">
                Connecting you with skilled carpenters across the UK for all your woodworking needs.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>Custom Furniture</li>
                <li>Kitchen Fitting</li>
                <li>Flooring Installation</li>
                <li>Built-in Storage</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Customers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Home</Link></li>
                <li><Link to="/how-it-works-customer" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/create-job" className="hover:text-primary">Post a Job</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Carpenters</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/for-tradesmen" className="hover:text-primary">Join Our Network</Link></li>
                <li><Link to="/tradesman-registration" className="hover:text-primary">Register Now</Link></li>
                <li><Link to="/how-it-works" className="hover:text-primary">How It Works</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};